import React, { MouseEvent, useRef } from 'react'
import * as RadixDialog from '@radix-ui/react-dialog'
import { useApplicationStore } from 'hooks/application'
import { getDiagnosticSnapshot } from 'helpers/diagnostics'
import Button from 'components/button'
import { closeAllDialogs } from 'components/dialog'
import { APP_URL } from 'app-constants'

/**
 * MortaSnackbar renders **two** visual styles:
 * 1. **Error** – Blocking modal centred with dark backdrop + “Dismiss” button.
 * 2. **All others** – Lightweight toast bottom‑right, no backdrop, no button.
 */
const MortaSnackbar: React.FC = () => {
  const { snackbarMessage, clearSnackbar, openSupportChat } = useApplicationStore()
  const { message, status, action } = snackbarMessage
  const dismissRef = useRef<HTMLButtonElement>(null)
  const returnFocusRef = useRef<HTMLElement | null>(null)

  // Hide component when inactive
  if (!status || status === 'noShow') return null

  // Compute message text
  const resolveMessage = () => {
    if (message && typeof message === 'string') return message
    if (status === 'error') return 'Uh oh – something went wrong… please refresh and try again'
    return 'That was successful!'
  }

  // Shared colour helpers (utility classes you already have)
  const backgroundClass =
    status === 'error'
      ? 'bg-red'
      : status === 'success'
        ? 'bg-green'
        : status === 'warning'
          ? 'bg-orange'
          : status === 'subscription'
            ? 'bg-alert'
            : 'bg-surface-sunken'
  // The alert yellow is light in both themes, so it takes on-light ink: `body` turns near-white
  // in dark mode and would vanish on it.
  const textClass = status === 'subscription' ? 'text-on-light' : 'text-on-solid'

  /* --------------------------------------------------------------------- */
  /* Blocking modal – **only** for errors                                   */
  /* --------------------------------------------------------------------- */
  if (status === 'error') {
    const offerSupport = snackbarMessage.offerSupport ?? !message

    // Helper: swallow + close
    const swallowAndDismiss = (e: MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
      if (e.nativeEvent && typeof e.nativeEvent.stopImmediatePropagation === 'function') {
        e.nativeEvent.stopImmediatePropagation()
      }
      clearSnackbar()
    }

    // Helper: swallow only (inside card)
    const swallowOnly = (e: MouseEvent) => {
      e.stopPropagation()
      if (e.nativeEvent && typeof e.nativeEvent.stopImmediatePropagation === 'function') {
        e.nativeEvent.stopImmediatePropagation()
      }
    }

    const handleOpenSupport = (e: MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
      if (e.nativeEvent && typeof e.nativeEvent.stopImmediatePropagation === 'function') {
        e.nativeEvent.stopImmediatePropagation()
      }
      const diagnostics = getDiagnosticSnapshot()
      openSupportChat({
        errorMessage: resolveMessage(),
        diagnostics,
        path: snackbarMessage.path,
        method: snackbarMessage.method
      })
      clearSnackbar()
      closeAllDialogs()
    }

    // A Radix dialog of its own, so it stacks above any dialog open beneath it: Radix lets only the topmost
    // dialog take presses, focus and screen readers, and an error drawn over one outside Radix got none of them.
    return (
      <RadixDialog.Root open onOpenChange={(open) => !open && clearSnackbar()}>
        <RadixDialog.Portal>
          {/* Backdrop */}
          <RadixDialog.Overlay
            className="fixed inset-0 z-[5000] bg-black/60 dark:bg-black/75"
            onMouseDown={swallowAndDismiss}
            onClick={swallowAndDismiss}
          />
          <div className="fixed inset-0 z-[5000] flex justify-center items-center pointer-events-none">
            {/* Error card */}
            <RadixDialog.Content
              role="alertdialog"
              className={`${backgroundClass} ${textClass} pointer-events-auto relative text-center rounded-lg max-w-[480px] w-[calc(100%_-_32px)] py-6 px-8 shadow-[0_8px_24px_rgba(0,0,0,0.15)] outline-none`}
              onMouseDown={swallowOnly}
              onClick={swallowOnly}
              // Dismiss, not AI Support or an action, is what Enter should do.
              onOpenAutoFocus={(e) => {
                e.preventDefault()
                returnFocusRef.current = document.activeElement as HTMLElement | null
                dismissRef.current?.focus()
              }}
              // Opened by the app rather than a trigger, so Radix has nothing to hand focus back to: back to
              // where it was when the error opened.
              onCloseAutoFocus={(e) => {
                e.preventDefault()
                returnFocusRef.current?.focus()
                returnFocusRef.current = null
              }}
            >
              <RadixDialog.Title asChild>
                <b>⚠️ Error</b>
              </RadixDialog.Title>
              :{' '}
              <RadixDialog.Description asChild>
                <span className="overflow-y-auto select-text mb-4 leading-[1.4] max-h-[200px]">{resolveMessage()}</span>
              </RadixDialog.Description>
              {action && (
                <div className="flex justify-center items-center gap-2 mt-4 text-[14px] opacity-80">{action}</div>
              )}
              {offerSupport && snackbarMessage.path && (
                <div>
                  <br />
                  <span className="text-[14px] opacity-80">
                    Technical info: {snackbarMessage.method ? `[${snackbarMessage.method}] ` : ''}
                    {APP_URL}
                    {snackbarMessage.path}{' '}
                  </span>
                </div>
              )}
              <div className="flex justify-center items-center gap-2 mt-5">
                {offerSupport && (
                  <Button
                    className="mr-2.5 min-w-[110px]"
                    internalType="outline"
                    onMouseDown={handleOpenSupport}
                    onClick={handleOpenSupport}
                  >
                    Open chat with AI Support
                  </Button>
                )}

                <Button
                  internalType="outline"
                  innerRef={dismissRef}
                  onMouseDown={swallowAndDismiss}
                  onClick={swallowAndDismiss}
                  className="min-w-[110px]"
                >
                  Dismiss
                </Button>
              </div>
            </RadixDialog.Content>
          </div>
        </RadixDialog.Portal>
      </RadixDialog.Root>
    )
  }

  /* --------------------------------------------------------------------- */
  /* Toast for success / warning / subscription / default                  */
  /* --------------------------------------------------------------------- */
  return (
    <div
      className={`fixed ${backgroundClass} ${textClass} p-4 rounded transition-all flex items-center gap-2 bottom-2.5 right-2.5 z-[4000]`}
    >
      <span>{resolveMessage()}</span>
      {/* Preserve original optional action, e.g. "Upgrade" button */}
      {action}
    </div>
  )
}

export default MortaSnackbar
